import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Alert,
    TextField,
    MenuItem,
    Button
} from '@mui/material';
import { styled } from '@mui/system';
import { FaSearch, FaFilter } from 'react-icons/fa';
import AppColors from '../../utils/AppColors';
import { getTransactions } from '../../services/api';

const Container = styled(Box)({
    padding: '2rem',
});

const PageTitle = styled(Typography)({
    fontSize: '2rem',
    fontWeight: 'bold',
    color: AppColors.primaryDark,
    marginBottom: '2rem',
});

const TransactionManagement = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');

    useEffect(() => {
        fetchTransactions();
    }, []);

    const fetchTransactions = async () => {
        try {
            const response = await getTransactions({ 
                search: searchTerm, 
                status: statusFilter,
                type: typeFilter 
            });
            setTransactions(response.transactions || []);
        } catch (err) {
            setError('Failed to fetch transactions');
            console.error('Error fetching transactions:', err);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return AppColors.successGreen;
            case 'pending': return AppColors.warningOrange;
            case 'failed': return AppColors.errorRed;
            case 'cancelled': return AppColors.neutralMedium;
            default: return AppColors.neutralMedium;
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'voucher_purchase': return AppColors.primaryYellow;
            case 'vendor_payment': return AppColors.primaryDark;
            case 'wallet_topup': return AppColors.successGreen;
            case 'refund': return AppColors.infoBluе;
            default: return AppColors.neutralMedium;
        }
    };

    if (loading) {
        return (
            <Container>
                <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container>
            <PageTitle>Transaction Management</PageTitle>
            
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Box display="flex" gap={2} mb={3}>
                    <TextField
                        placeholder="Search transactions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <FaSearch style={{ marginRight: 8 }} />,
                        }}
                        sx={{ flexGrow: 1 }}
                    />
                    <TextField
                        select
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        sx={{ minWidth: 120 }}
                    >
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                    </TextField>
                    <TextField
                        select
                        label="Type"
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        sx={{ minWidth: 150 }}
                    >
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value="voucher_purchase">Voucher Purchase</MenuItem>
                        <MenuItem value="vendor_payment">Vendor Payment</MenuItem>
                        <MenuItem value="wallet_topup">Wallet Top-up</MenuItem>
                        <MenuItem value="refund">Refund</MenuItem>
                    </TextField>
                    <Button
                        variant="contained"
                        onClick={fetchTransactions}
                        startIcon={<FaFilter />}
                        sx={{
                            backgroundColor: AppColors.primaryDark,
                            '&:hover': { backgroundColor: AppColors.primaryYellow, color: AppColors.primaryDark }
                        }}
                    >
                        Filter
                    </Button>
                </Box>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Transaction ID</TableCell>
                                <TableCell>User</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Payment Method</TableCell>
                                <TableCell>Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {transactions.map((transaction) => (
                                <TableRow key={transaction.id}>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                            {transaction.transaction_id || transaction.id}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{transaction.user_name || 'N/A'}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={transaction.type?.replace('_', ' ') || 'Unknown'} 
                                            sx={{ 
                                                backgroundColor: `${getTypeColor(transaction.type)}20`,
                                                color: getTypeColor(transaction.type),
                                                textTransform: 'capitalize'
                                            }} 
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">
                                            AED {transaction.amount}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={transaction.status} 
                                            sx={{ 
                                                backgroundColor: `${getStatusColor(transaction.status)}20`,
                                                color: getStatusColor(transaction.status),
                                                textTransform: 'capitalize'
                                            }} 
                                        />
                                    </TableCell>
                                    <TableCell>{transaction.payment_method || 'N/A'}</TableCell>
                                    <TableCell>
                                        {new Date(transaction.created_at).toLocaleString()}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Container>
    );
};

export default TransactionManagement;
